import ApplicationSubmissionStatusPoller from 'api/utils/ApplicationSubmissionStatusPoller'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

import submitApplication from './submitApplication'
import { DecisionResult } from './types.d'

// consider a timeout after 3 minutes and 30 seconds
const DECISION_TIMEOUT_SECONDS = 210
// start polling after 5 seconds
const DECISION_POLL_INTERVAL_SECONDS = 5
// on every fetch, increase the polling interval by 50%
const DECISION_POLL_INTERVAL_MULTIPLIER = 1.5
// do not allow polling time to be more than 10 seconds
const DECISION_MAX_POLL_INTERVAL_SECONDS = 10

const logTimeElapsed = (
    startTime: Date,
    isAsyncSubmission: boolean,
    applicationUuid: string,
    applicationStatus: string,
) => {
    const endTime = new Date()
    const timeElapsedSeconds = (endTime.getTime() - startTime.getTime()) / 1000

    OctaneLogger.info(
        `Application submission took ${timeElapsedSeconds} seconds`,
        {
            isAsyncSubmission,
            timeElapsedSeconds,
            applicationUuid,
            applicationStatus,
        },
    )
}

/**
 * Submits an application and waits for its decision with a timeout mechanism.
 *
 * Throws the `ApplicationSubmissionTimeoutError` if the decision takes more time than expected.
 * Throws the `ApplicationSubmissionError` if the decision results in an unexpected error
 */
export default async function getFinalDecision(
    uuid: string,
    requestAsyncSubmission = true,
): Promise<DecisionResult> {
    const beforeSubmission = new Date()

    const decisionResult = await submitApplication(uuid, {
        asyncSubmission: requestAsyncSubmission,
        timeoutSeconds: DECISION_TIMEOUT_SECONDS,
    })

    // not running async, then return immediately
    if (!decisionResult.is_async_submission) {
        logTimeElapsed(beforeSubmission, false, uuid, decisionResult.status)

        return {
            applicationStatus: decisionResult.status,
            asyncSubmission: decisionResult.is_async_submission,
        }
    }

    // for async decisioning, we need to keep doing polling until the decision is processed
    const poller = new ApplicationSubmissionStatusPoller({
        applicationUuid: uuid,
        timeoutSeconds: DECISION_TIMEOUT_SECONDS,
        pollIntervalSeconds: DECISION_POLL_INTERVAL_SECONDS,
        maxPollIntervalSeconds: DECISION_MAX_POLL_INTERVAL_SECONDS,
        pollIntervalMultiplier: DECISION_POLL_INTERVAL_MULTIPLIER,
    })

    const applicationStatus = await poller.pollUntilCompletion()

    logTimeElapsed(beforeSubmission, true, uuid, applicationStatus)

    return {
        applicationStatus,
        asyncSubmission: true,
    }
}
